import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { vendorService, type Vendor } from '../services/vendorService';
import { getSupabaseClient, isSupabaseConfigured } from '../services/supabaseClient';
import { PeptideCard, VendorProduct } from '../components/PeptideCard';
import { SEO } from '../components/SEO';
import { trackVendorClick } from '../services/vendorClickTrackingService';
import { generateOrganizationSchema, getBaseUrl } from '../utils/seo';
import { getVendorWebsiteLinkText } from '../utils/vendorWebsiteLink';
import './StoreDetailsPage.css';

/**
 * Store Details Page showing comprehensive vendor information
 */
export function StoreDetailsPage() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [peptideCount, setPeptideCount] = useState<number>(0);
  const [peptides, setPeptides] = useState<VendorProduct[]>([]);
  const [peptidesExpanded, setPeptidesExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [peptideCountLoading, setPeptideCountLoading] = useState(true);
  const [peptidesLoading, setPeptidesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch vendor data
  useEffect(() => {
    const loadVendor = async () => {
      if (!vendorId) {
        setError('Vendor ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const vendors = await vendorService.getVendors();
        const foundVendor = vendors.find((v) => v.id === vendorId);

        if (!foundVendor) {
          setError('Vendor not found');
          setLoading(false);
          return;
        }

        setVendor(foundVendor);
      } catch (err) {
        console.error('Failed to load vendor:', err);
        setError('Failed to load vendor. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadVendor();
  }, [vendorId]);

  // Fetch peptide count
  useEffect(() => {
    const loadPeptideCount = async () => {
      if (!vendorId || !isSupabaseConfigured()) {
        setPeptideCountLoading(false);
        return;
      }

      try {
        setPeptideCountLoading(true);
        const supabase = getSupabaseClient();
        if (!supabase) {
          setPeptideCountLoading(false);
          return;
        }

        const { count, error: countError } = await supabase
          .from('vendor_peptide_offers')
          .select('*', { count: 'exact', head: true })
          .eq('vendor_id', vendorId);

        if (countError) throw countError;

        setPeptideCount(count || 0);
      } catch (err) {
        console.error('Failed to load peptide count:', err);
      } finally {
        setPeptideCountLoading(false);
      }
    };

    if (vendorId) {
      loadPeptideCount();
    }
  }, [vendorId]);

  const handleWebsiteClick = () => {
    if (!vendor?.websiteUrl) {
      return;
    }

    trackVendorClick({
      vendor_id: vendor.id,
      offer_id: null,
      affiliate_url: vendor.websiteUrl,
      source: 'store_details_page',
      device_platform: 'web',
      peptide_name: null,
      event_type: 'vendor_click',
    }).catch((err) => {
      if (import.meta.env.DEV) {
        console.error('Failed to track vendor click:', err);
      }
    });
  };

  // Fetch peptides when section is expanded
  useEffect(() => {
    const loadPeptides = async () => {
      if (!peptidesExpanded || !vendorId || !isSupabaseConfigured()) {
        return;
      }

      try {
        setPeptidesLoading(true);
        const supabase = getSupabaseClient();
        if (!supabase) {
          return;
        }

        const { data, error: peptidesError } = await supabase
          .from('vendor_peptide_offers')
          .select('*, vendors(name, logo_url, coupon_code, coupon_description, website_url, scraped_coupon_code, scraped_coupon_discount, scraped_coupon_expires_at, scraped_coupon_last_seen, scraped_coupon_source)')
          .eq('vendor_id', vendorId);

        if (peptidesError) throw peptidesError;

        const transformedPeptides: VendorProduct[] = (data || []).map((row: any) => ({
          id: row.id,
          vendor_id: row.vendor_id,
          vendor_name: row.vendors?.name || 'Unknown Vendor',
          canonical_peptide: row.canonical_peptide,
          peptide_name_raw: row.peptide_name_raw,
          product_name: row.product_name,
          product_url: row.product_url,
          price: Number(row.price),
          currency: row.currency || 'USD',
          vial_size_text: row.vial_size_text,
          vial_size_mg: row.vial_size_mg ? Number(row.vial_size_mg) : null,
          price_per_mg: row.price_per_mg ? Number(row.price_per_mg) : null,
          coupon_code: row.vendors?.coupon_code || null,
          coupon_description: row.vendors?.coupon_description || null,
          scraped_coupon_code: row.vendors?.scraped_coupon_code || null,
          scraped_coupon_discount: row.vendors?.scraped_coupon_discount || null,
          vendor_website_url: row.vendors?.website_url || null,
          vendor_logo_url: row.vendors?.logo_url || null,
          affiliate_url: row.affiliate_url || null,
        }));

        // Sort alphabetically by peptide name
        transformedPeptides.sort((a, b) => {
          const nameA = (a.canonical_peptide || a.peptide_name_raw || a.product_name || '').toLowerCase();
          const nameB = (b.canonical_peptide || b.peptide_name_raw || b.product_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setPeptides(transformedPeptides);
      } catch (err) {
        console.error('Failed to load peptides:', err);
      } finally {
        setPeptidesLoading(false);
      }
    };

    loadPeptides();
  }, [peptidesExpanded, vendorId]);

  const handlePeptidesClick = () => {
    setPeptidesExpanded(!peptidesExpanded);
  };

  const handleProductClick = (product: VendorProduct) => {
    const url = product.affiliate_url || product.product_url;
    if (url) {
      const peptideName = product.canonical_peptide || product.peptide_name_raw || null;
      trackVendorClick({
        vendor_id: product.vendor_id,
        offer_id: product.id,
        affiliate_url: url,
        source: 'store_details_page',
        device_platform: 'web',
        peptide_name: peptideName,
        event_type: 'vendor_click',
      }).catch((err) => {
        if (import.meta.env.DEV) {
          console.error('Failed to track vendor click:', err);
        }
      });

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Get vendor initials for placeholder
  const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate structured data
  const structuredData = useMemo(() => {
    if (!vendor) return [];
    const baseUrl = getBaseUrl();
    return [generateOrganizationSchema(baseUrl)];
  }, [vendor]);

  const pageTitle = vendor
    ? `${vendor.name} - Store Details - Peptide Finder`
    : 'Store Details - Peptide Finder';
  const pageDescription = vendor
    ? `View detailed information about ${vendor.name}, including vendor description, peptide offerings, and more.`
    : 'View store details and vendor information.';

  if (loading) {
    return (
      <>
        <SEO title={pageTitle} description={pageDescription} structuredData={structuredData} />
        <div className="store-details-page">
          <div className="store-details-container">
            <div className="store-details-loading" role="status" aria-live="polite">
              <p>Loading store details...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !vendor) {
    return (
      <>
        <SEO title="Store Not Found - Peptide Finder" description={pageDescription} structuredData={structuredData} />
        <div className="store-details-page">
          <div className="store-details-container">
            <div className="store-details-error" role="alert">
              <p>{error || 'Store not found'}</p>
              <Link to="/vendors" className="store-details-back-link">
                ← Back to Vendors
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const websiteLinkText = getVendorWebsiteLinkText(vendor);

  return (
    <>
      <SEO title={pageTitle} description={pageDescription} structuredData={structuredData} />
      <div className="store-details-page">
        <div className="store-details-container">
          {/* Header */}
          <header className="store-details-header">
            <Link to="/vendors" className="store-details-back-link">
              ←
            </Link>
            <h1 className="store-details-title">Store Details</h1>
          </header>

          {/* Vendor Header Card */}
          <section className="store-details-vendor-header-card">
            <div className="store-details-vendor-header-content">
              {vendor.logoUrl ? (
                <img
                  src={vendor.logoUrl}
                  alt={`${vendor.name} logo`}
                  className="store-details-vendor-logo"
                  loading="lazy"
                />
              ) : (
                <div className="store-details-vendor-logo-placeholder">
                  {getInitials(vendor.name)}
                </div>
              )}
              <h2 className="store-details-vendor-name">{vendor.name}</h2>
            </div>
            {vendor.tags && vendor.tags.length > 0 && (
              <div className="store-details-vendor-tags">
                {vendor.tags.map((tag, index) => (
                  <span key={index} className="store-details-vendor-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* About Section */}
          {vendor.description && (
            <section className="store-details-section-card">
              <h3 className="store-details-section-title">ABOUT THIS VENDOR</h3>
              <p className="store-details-description">{vendor.description}</p>
            </section>
          )}

          {/* Peptides Section */}
          <section className="store-details-section-card">
            <div 
              className="store-details-section-header store-details-section-clickable"
              onClick={handlePeptidesClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePeptidesClick();
                }
              }}
              aria-label={`${peptidesExpanded ? 'Collapse' : 'Expand'} ${peptideCount} peptides from this vendor`}
              aria-expanded={peptidesExpanded}
            >
              <h3 className="store-details-section-title">PEPTIDES FROM THIS VENDOR</h3>
              <div className="store-details-peptides-count">
                {peptideCountLoading ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    <span>{peptideCount} peptides</span>
                    <span className={`store-details-chevron ${peptidesExpanded ? 'store-details-chevron-expanded' : ''}`}>
                      {peptidesExpanded ? '∧' : '∨'}
                    </span>
                  </>
                )}
              </div>
            </div>
            {peptidesExpanded && (
              <div className="store-details-peptides-list">
                {peptidesLoading ? (
                  <div className="store-details-peptides-loading">
                    <p>Loading peptides...</p>
                  </div>
                ) : peptides.length === 0 ? (
                  <div className="store-details-peptides-empty">
                    <p>No peptides found.</p>
                  </div>
                ) : (
                  <div className="store-details-peptides-grid">
                    {peptides.map((peptide) => (
                      <PeptideCard
                        key={peptide.id}
                        product={peptide}
                        onPress={() => handleProductClick(peptide)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* User Reviews Section */}
          <section className="store-details-section-card">
            <h3 className="store-details-section-title">USER REVIEWS</h3>
            <div className="store-details-coming-soon">
              <span className="store-details-icon" aria-hidden="true">⭐</span>
              <p className="store-details-coming-soon-text">Coming Soon</p>
              <p className="store-details-coming-soon-desc">
                User reviews and ratings will be available here soon.
              </p>
            </div>
          </section>

          {/* Certificate of Analysis Section */}
          <section className="store-details-section-card">
            <h3 className="store-details-section-title">CERTIFICATE OF ANALYSIS</h3>
            <div className="store-details-coming-soon">
              <span className="store-details-icon" aria-hidden="true">📄</span>
              <p className="store-details-coming-soon-text">Coming Soon</p>
              <p className="store-details-coming-soon-desc">
                Certificate of Analysis (COA) documents will be available here soon.
              </p>
            </div>
          </section>

          {/* Floating Visit Website link */}
          {vendor.websiteUrl && (
            <a
              href={vendor.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWebsiteClick}
              className="store-details-visit-button"
              aria-label={`Visit ${vendor.name} website`}
            >
              {websiteLinkText}
            </a>
          )}
        </div>
      </div>
    </>
  );
}
